import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Bell, BellOff, BellRing, Smartphone, Loader2, SlidersHorizontal } from 'lucide-react';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { getBackendUrl } from '../utils/getBackendUrl';
import {
  isPushSupported, isStandalone, isIOS, getPushState,
  subscribeToPush, unsubscribeFromPush, sendTestPush,
} from '../utils/pushNotifications';

const API = `${getBackendUrl()}/api`;

const ALERT_TYPES = [
  { key: 'direct_messages', label: 'Direct Messages', desc: 'Someone sends you a message' },
  { key: 'crew_chat', label: 'Crew Chat', desc: 'New messages in project crew chats' },
  { key: 'project_notifications', label: 'Project Updates', desc: 'Photo updates & project changes' },
  { key: 'notes', label: 'Notes', desc: 'A note is left for you' },
  { key: 'communications', label: 'Communications', desc: 'Logged calls, texts & emails to you' },
];

const ADMIN_ALERT_TYPES = [
  { key: 'photo_approvals', label: 'Photo Approvals', desc: 'A photo is waiting for approval', adminOnly: true },
  { key: 'registrations', label: 'Registrations & Suggested Clients', desc: 'New sign-ups or client suggestions to approve', adminOnly: true },
];

export const PushNotificationSettings = ({ user }) => {
  const [state, setState] = useState('loading'); // loading | unsupported | denied | not_subscribed | subscribed
  const [busy, setBusy] = useState(false);
  const [prefs, setPrefs] = useState(null);

  useEffect(() => {
    getPushState().then(setState);
  }, []);

  useEffect(() => {
    if (state === 'subscribed') {
      axios.get(`${API}/push/preferences`).then((r) => setPrefs(r.data)).catch(() => {});
    }
  }, [state]);

  const togglePref = async (key) => {
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    try {
      await axios.put(`${API}/push/preferences`, next);
    } catch {
      setPrefs(prefs);
      toast.error('Could not save preference');
    }
  };

  const enable = async () => {
    setBusy(true);
    try {
      await subscribeToPush();
      setState('subscribed');
      toast.success('Alerts enabled! The app icon will now show a red badge for unread items.');
    } catch (e) {
      if (Notification.permission === 'denied') {
        setState('denied');
        toast.error('Notifications are blocked. Enable them in your device settings for this app.');
      } else {
        toast.error(e.message || 'Could not enable notifications');
      }
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await unsubscribeFromPush();
      setState('not_subscribed');
      if ('clearAppBadge' in navigator) navigator.clearAppBadge().catch(() => {});
      toast.success('Alerts disabled on this device');
    } catch {
      toast.error('Could not disable notifications');
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    setBusy(true);
    try {
      await sendTestPush();
      toast.success('Test alert sent — you should see it in a moment');
    } catch {
      toast.error('Test failed');
    } finally {
      setBusy(false);
    }
  };

  const iosNotInstalled = isIOS() && !isStandalone();
  const isAdmin = user?.role === 'admin' || user?.role === 'supervisor';

  return (
    <div className="glass-card p-6 mb-6" data-testid="push-notification-settings">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-3 bg-red-500/20 rounded-lg">
          <BellRing className="w-6 h-6 text-red-400" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-white">App Alerts &amp; Icon Badge</h2>
          <p className="text-sm text-slate-400">
            Get notified of new messages &amp; alerts — a red badge shows on the app icon
          </p>
        </div>
      </div>

      {state === 'loading' && (
        <div className="flex items-center gap-2 text-slate-400 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Checking device…
        </div>
      )}

      {state === 'unsupported' && (
        <div className="bg-slate-700/50 border border-slate-600 rounded-lg p-4 text-sm text-slate-300" data-testid="push-unsupported-msg">
          <p className="font-semibold mb-1 flex items-center gap-2"><Smartphone className="w-4 h-4" /> Not available in this browser</p>
          <p>
            {isIOS()
              ? 'On iPhone/iPad: open this site in Safari, tap Share → "Add to Home Screen", then open the app from your home screen and come back here to enable alerts.'
              : 'This browser does not support push notifications. Try Chrome, Edge, or add the app to your home screen.'}
          </p>
        </div>
      )}

      {state === 'denied' && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-sm text-red-300" data-testid="push-denied-msg">
          Notifications are blocked for this app. Enable them in your device settings
          (iPhone: Settings → Notifications → Dawal Connect), then reload.
        </div>
      )}

      {state === 'not_subscribed' && (
        <div className="space-y-3">
          {iosNotInstalled && (
            <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3 text-xs text-orange-300">
              Tip: On iPhone, first add this app to your Home Screen (Share → "Add to Home Screen") —
              the red icon badge only works from the installed app.
            </div>
          )}
          <Button
            onClick={enable}
            disabled={busy}
            className="bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white min-h-[44px]"
            data-testid="enable-push-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bell className="w-4 h-4 mr-2" />}
            Enable Alerts on This Device
          </Button>
        </div>
      )}

      {state === 'subscribed' && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-emerald-400 text-sm font-semibold" data-testid="push-enabled-status">
            <span className="w-2.5 h-2.5 bg-emerald-400 rounded-full inline-block" />
            Alerts are ON for this device
          </div>
          <p className="text-xs text-slate-400">
            You'll get a notification for new messages, crew chat, notes and approvals —
            and the app icon shows a red badge with your unread count, even when the app is closed.
          </p>

          {prefs && (
            <div className="bg-slate-800/40 border border-slate-700 rounded-lg p-4 space-y-3" data-testid="alert-preferences">
              <p className="text-sm font-bold text-white flex items-center gap-2">
                <SlidersHorizontal className="w-4 h-4 text-orange-400" />
                Which alerts should buzz this phone?
              </p>
              {[...ALERT_TYPES, ...(isAdmin ? ADMIN_ALERT_TYPES : [])].map((t) => (
                <div key={t.key} className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-200">
                      {t.label}
                      {t.adminOnly && <span className="ml-1.5 text-[10px] font-bold text-orange-400 uppercase">Admin</span>}
                    </p>
                    <p className="text-xs text-slate-400">{t.desc}</p>
                  </div>
                  <Switch
                    checked={prefs[t.key] !== false}
                    onCheckedChange={() => togglePref(t.key)}
                    data-testid={`alert-pref-${t.key}`}
                  />
                </div>
              ))}
              <p className="text-[11px] text-slate-500">
                Turning one off stops phone notifications for it — the red icon badge still counts everything.
              </p>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <Button
              onClick={test}
              disabled={busy}
              variant="outline"
              className="border-slate-600 text-slate-300 hover:bg-slate-700 min-h-[44px]"
              data-testid="test-push-btn"
            >
              <BellRing className="w-4 h-4 mr-2" /> Send Test Alert
            </Button>
            <Button
              onClick={disable}
              disabled={busy}
              variant="outline"
              className="border-red-600 text-red-400 hover:bg-red-600 hover:text-white min-h-[44px]"
              data-testid="disable-push-btn"
            >
              <BellOff className="w-4 h-4 mr-2" /> Turn Off
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
